import { Slider, type SliderProps, useTheme } from "@mui/material";
import { useThrottledCallback } from "@tanstack/react-pacer/throttler";
import { useContext } from "react";

import { AudioAppContext } from "@/models/AudioAppContextProvider";
import type { AudioPlayerProps } from "../AudioPlayer";

/** Seeks are paced to this while a scrub is in flight. */
const SCRUB_THROTTLE_MS = 250;

type ChangeCurrentTimeProps = (args: {
  audioElement: HTMLAudioElement;
  duration?: number;
  feedItemId?: string;
}) => SliderProps["onChange"];

export interface PlaybackTimelineProps extends Omit<SliderProps, "size"> {
  className?: string;
  duration: number;
  feedItemId: string;
  position: number;
  size?: AudioPlayerProps["size"];
}

export default function PlaybackTimeline({
  className = "",
  duration = 0,
  feedItemId,
  position,
  size,
  ...rest
}: PlaybackTimelineProps) {
  const { audioElement } = useContext(AudioAppContext);
  const theme = useTheme();

  // Trailing edge as well as leading, unlike the throttle this replaced: that one was rebuilt on
  // every render, so the seek that followed a re-render always landed and the drop was invisible.
  // A throttle that actually holds across renders needs the trailing edge, or releasing the thumb
  // mid-window leaves playback up to a window's worth of scrubbing away from where it was dropped.
  const changeCurrentTimeForTimeline = useThrottledCallback(
    (_event: Event, value: number | number[]) => {
      if (!audioElement) return;
      const currentPosition = (duration / 100) * (Array.isArray(value) ? (value[0] ?? 0) : value);
      if (audioElement.fastSeek instanceof Function) {
        audioElement.fastSeek(currentPosition);
      } else {
        audioElement.currentTime = currentPosition;
      }
    },
    { wait: SCRUB_THROTTLE_MS },
  );

  return (
    <Slider
      role="slider"
      className={className}
      onChange={changeCurrentTimeForTimeline}
      size={size === "large" ? "medium" : (size ?? "small")}
      value={position}
      sx={{
        color: theme.palette.text.primary,
        ".MuiSlider-rail": { height: "4px", borderRadius: 0 },
        ".MuiSlider-track": {
          height: "4px",
          borderRadius: 0,
        },
      }}
      {...rest}
    />
  );
}
